import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
  MapPinIcon,
  EyeIcon,
  TrashIcon,
  CreditCardIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { burialPlotBookingService } from '../../services/burial-plot-booking.service'
import { formatShortDate, formatStatus, getStatusColor, getBurialPlotPaymentStatus } from '../../utils/formatters'
import LoadingSpinner from '../../components/LoadingSpinner'

function BurialPlotBookingsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [bookings, setBookings] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [sortBy, setSortBy] = useState<'createdAt' | 'scheduledDateTime'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const limit = 10

  const fetchBookings = async () => {
    setIsLoading(true)
    try {
      const result = await burialPlotBookingService.getUserBookings()
      console.log('🔍 DEBUG - Bookings loaded:', result)
      result.forEach((booking, index) => {
        const paymentStatus = getBurialPlotPaymentStatus(booking)
        console.log(`🔍 DEBUG - Booking ${index + 1} (${booking.id}):`, {
          status: booking.status,
          transferReference: booking.transferReference,
          paymentStatus: booking.payment?.status,
          verificationStatus: booking.payment?.verificationStatus,
          analysis: paymentStatus
        })
      })
      setBookings(result)
      setTotal(result.length)
      setTotalPages(Math.ceil(result.length / limit))
    } catch (error) {
      console.error('Failed to fetch burial plot bookings:', error)
      // Show toast or error message
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchBookings()
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: Implement search functionality (currently searchQuery is not used in API)
    fetchBookings()
  }

  const handleSort = (column: 'createdAt' | 'scheduledDateTime') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
    setCurrentPage(1)
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status === statusFilter ? '' : status)
    setCurrentPage(1)
  }

  const handleCancelBooking = async (bookingId: string) => {
    const reason = window.prompt('Please provide a reason for cancelling this booking:')
    if (reason === null) return // User cancelled

    try {
      await burialPlotBookingService.cancelBooking(bookingId, reason)
      // Refresh the bookings list
      fetchBookings()
      // Show success message (you could add a toast notification here)
    } catch (error) {
      console.error('Failed to cancel booking:', error)
      // Show error message (you could add a toast notification here)
    }
  }

  const handleDeleteBooking = async (bookingId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this booking? This action cannot be undone and will remove all booking data.')) {
      return
    }

    try {
      await burialPlotBookingService.deleteBooking(bookingId)
      // Refresh the bookings list
      fetchBookings()
      // Show success message (you could add a toast notification here)
    } catch (error) {
      console.error('Failed to delete booking:', error)
      // Show error message (you could add a toast notification here)
    }
  }

  const getStatusOptions = () => {
    const statuses = bookings.map(booking => booking.status)
    return [...new Set(statuses)]
  }

  
  // Filter and sort bookings based on current filters
  const getFilteredAndSortedBookings = () => {
    let filtered = bookings

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(booking => booking.status === statusFilter)
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(booking =>
        booking.id?.toLowerCase().includes(query) ||
        booking.burialArea?.toLowerCase().includes(query) ||
        booking.provider?.companyName?.toLowerCase().includes(query)
      )
    }

    // Sort bookings
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy]
      let bValue: any = b[sortBy]

      // Handle date sorting
      if (sortBy === 'createdAt' || sortBy === 'scheduledDateTime') {
        aValue = new Date(aValue)
        bValue = new Date(bValue)
      }

      if (sortOrder === 'desc') {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      } else {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      }
    })

    // Apply pagination
    const startIndex = (currentPage - 1) * limit
    const endIndex = startIndex + limit
    return filtered.slice(startIndex, endIndex)
  }

  const filteredBookings = getFilteredAndSortedBookings()

  if (isLoading && bookings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Burial Plot Bookings</h1>
            <p className="mt-1 text-sm text-neutral-600">
              Manage and track all burial plot reservations
            </p>
          </div>
          <Link
            to="/bookings/burial-plot"
            className="btn-primary flex items-center gap-2"
          >
            <PlusCircleIcon className="h-5 w-5" />
            New Booking
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="text"
                className="input pl-10"
                placeholder="Search by booking ID, cemetery, or provider..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <button
            type="submit"
            className="btn-outline"
          >
            Search
          </button>
        </form>

        {/* Status Filter */}
        <div className="flex gap-2 flex-wrap">
          <span className="text-sm text-neutral-600 py-2">Filter by status:</span>
          {getStatusOptions().map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                statusFilter === status
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-neutral-300 text-neutral-600 hover:border-neutral-400'
              }`}
            >
              {formatStatus(status)}
            </button>
          ))}
          {statusFilter && (
            <button
              onClick={() => {
                setStatusFilter('')
                setCurrentPage(1)
              }}
              className="px-3 py-1 text-sm rounded-full border border-neutral-300 text-neutral-600 hover:border-neutral-400"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {/* Bookings List */}
      <div className="card">
        <div className="card-body">
          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <MapPinIcon className="mx-auto h-12 w-12 text-neutral-400" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900">No burial plot bookings found</h3>
              <p className="mt-1 text-sm text-neutral-500">
                {searchQuery || statusFilter ? 'Try adjusting your search terms or filters.' : 'Get started by creating your first burial plot booking.'}
              </p>
              <div className="mt-6">
                {!searchQuery && !statusFilter && (
                  <Link
                    to="/bookings/burial-plot"
                    className="btn-primary"
                  >
                    Create New Booking
                  </Link>
                )}
              </div>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-12">
              <MapPinIcon className="mx-auto h-12 w-12 text-neutral-400" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900">No bookings match your criteria</h3>
              <p className="mt-1 text-sm text-neutral-500">
                Try adjusting your search terms or filters.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Booking ID
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Cemetery
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Plot Details
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Provider
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 whitespace-nowrap"
                          onClick={() => handleSort('createdAt')}
                      >
                        Created
                        {sortBy === 'createdAt' && (
                          <span className="ml-1">
                            {sortOrder === 'desc' ? '↓' : '↑'}
                          </span>
                        )}
                      </th>
                      <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-neutral-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="max-w-[150px] truncate" title={booking.id}>
                            {booking.id}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          <div className="max-w-[120px] truncate" title={booking.burialArea}>
                            {booking.burialArea}
                          </div>
                          {booking.scheduledDateTime && (
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(booking.scheduledDateTime).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          <div className="max-w-[140px]">
                            {booking.plot?.plotNumber && (
                              <div className="font-medium truncate">Plot {booking.plot.plotNumber}</div>
                            )}
                            {booking.plot?.section && (
                              <div className="text-gray-500 text-xs truncate">{booking.plot.section}</div>
                            )}
                            {booking.burialSize && (
                              <div className="text-gray-500 text-xs">Size: {booking.burialSize}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="max-w-[120px] truncate" title={booking.provider?.companyName || 'N/A'}>
                            {booking.provider?.companyName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="max-w-[100px]">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                              {formatStatus(booking.status)}
                            </span>
                            {(() => {
                              const paymentStatus = getBurialPlotPaymentStatus(booking);
                              if (!paymentStatus.isPaymentVerified) {
                                return (
                                  <div className="mt-1">
                                    <span className={`inline-flex px-1 py-0.5 text-xs rounded-full ${
                                      paymentStatus.isPaymentRequired
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {paymentStatus.statusMessage}
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="max-w-[80px]">
                            {formatShortDate(booking.createdAt)}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex items-center justify-center gap-1 min-w-[120px]">
                            {(() => {
                              const paymentStatus = getBurialPlotPaymentStatus(booking);
                              if (paymentStatus.actionRequired) {
                                return (
                                  <Link
                                    to={`/bookings/burial-plot/payment/${booking.id}`}
                                    className="text-green-600 hover:text-green-900 p-1"
                                    title="Complete Payment"
                                  >
                                    <CreditCardIcon className="h-4 w-4" />
                                  </Link>
                                );
                              }
                              return null;
                            })()}
                            <Link
                              to={`/bookings/burial-plot/${booking.id}`}
                              className="text-primary-600 hover:text-primary-900 p-1"
                              title="View Details"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Link>
                            {/* Only show Cancel and Delete for bookings that aren't already cancelled or completed */}
                            {booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED' && (
                              <>
                                <button
                                  onClick={() => handleCancelBooking(booking.id)}
                                  className="text-yellow-600 hover:text-yellow-900 p-1"
                                  title="Cancel Booking"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteBooking(booking.id)}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="Delete Booking"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {/* Show Delete for cancelled bookings (user might want to clean up) */}
                            {booking.status === 'CANCELLED' && (
                              <button
                                onClick={() => handleDeleteBooking(booking.id)}
                                className="text-red-600 hover:text-red-900 p-1"
                                title="Delete Booking"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {filteredBookings.map((booking) => (
                  <div key={booking.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate" title={booking.id}>
                          {booking.id}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Created: {formatShortDate(booking.createdAt)}
                        </p>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                          {formatStatus(booking.status)}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Cemetery:</span>
                        <span className="font-medium text-right truncate max-w-[120px]">{booking.burialArea}</span>
                      </div>

                      {booking.plot?.plotNumber && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Plot:</span>
                          <span className="font-medium">{booking.plot.plotNumber}</span>
                        </div>
                      )}

                      {booking.plot?.section && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Section:</span>
                          <span className="font-medium">{booking.plot.section}</span>
                        </div>
                      )}

                      <div className="flex justify-between">
                        <span className="text-gray-500">Provider:</span>
                        <span className="font-medium text-right truncate max-w-[120px]">
                          {booking.provider?.companyName || 'N/A'}
                        </span>
                      </div>

                      {(() => {
                        const paymentStatus = getBurialPlotPaymentStatus(booking);
                        if (!paymentStatus.isPaymentVerified) {
                          return (
                            <div className="pt-2">
                              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                                paymentStatus.isPaymentRequired
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {paymentStatus.statusMessage}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-end gap-2">
                        {(() => {
                          const paymentStatus = getBurialPlotPaymentStatus(booking);
                          if (paymentStatus.actionRequired) {
                            return (
                              <Link
                                to={`/bookings/burial-plot/payment/${booking.id}`}
                                className="text-green-600 hover:text-green-900 p-2 bg-green-50 rounded-lg"
                                title="Complete Payment"
                              >
                                <CreditCardIcon className="h-5 w-5" />
                              </Link>
                            );
                          }
                          return null;
                        })()}
                        <Link
                          to={`/bookings/burial-plot/${booking.id}`}
                          className="text-primary-600 hover:text-primary-900 p-2 bg-primary-50 rounded-lg"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </Link>
                        {/* Only show Cancel and Delete for bookings that aren't already cancelled or completed */}
                        {booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED' && (
                          <>
                            <button
                              onClick={() => handleCancelBooking(booking.id)}
                              className="text-yellow-600 hover:text-yellow-900 p-2 bg-yellow-50 rounded-lg"
                              title="Cancel Booking"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteBooking(booking.id)}
                              className="text-red-600 hover:text-red-900 p-2 bg-red-50 rounded-lg"
                              title="Delete Booking"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </>
                        )}
                        {/* Show Delete for cancelled bookings (user might want to clean up) */}
                        {booking.status === 'CANCELLED' && (
                          <button
                            onClick={() => handleDeleteBooking(booking.id)}
                            className="text-red-600 hover:text-red-900 p-2 bg-red-50 rounded-lg"
                            title="Delete Booking"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="card-footer">
              <div className="flex items-center justify-between">
                <div className="text-sm text-neutral-700">
                  Showing {((currentPage - 1) * limit) + 1} to{' '}
                  {Math.min(currentPage * limit, total)} of{' '}
                  {total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="btn-outline px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-neutral-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-outline px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BurialPlotBookingsPage